import { useState, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ChevronDown, ChevronRight, KeyRound, Phone, Plus, PowerOff, Power as PowerOn } from 'lucide-react'
import axios from 'axios'
import { toast } from 'sonner'
import {
  createChannelAccount,
  fetchChannelAccounts,
  updateChannelAccount,
  type ChannelAccount,
} from '../api'
import { inputClass } from '../styles'
import { Button } from './ui/button'
import { Badge } from './ui/badge'
import { Skeleton } from './ui/skeleton'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog'
import { EmbeddedSignupButton } from './EmbeddedSignupButton'

interface Props {
  activeSociedadId: string
}

const emptyForm = { display_name: '', phone_number: '', meta_phone_number_id: '', meta_waba_id: '', access_token: '' }

// Si la conexión automática con Meta está configurada, el formulario manual
// arranca colapsado (es el camino de respaldo/avanzado); si no, arranca
// visible porque es el único camino disponible todavía.
const EMBEDDED_SIGNUP_CONFIGURED = Boolean(
  (import.meta.env.VITE_META_APP_ID as string | undefined) && (import.meta.env.VITE_META_CONFIG_ID as string | undefined),
)

const TOKEN_EXPIRATION_WARNING_DAYS = 7

function tokenExpirationWarning(tokenExpiresAt: string | null): string | null {
  if (!tokenExpiresAt) return null
  const msRemaining = new Date(tokenExpiresAt).getTime() - Date.now()
  if (msRemaining <= 0) return 'Conexión vencida'
  if (msRemaining < TOKEN_EXPIRATION_WARNING_DAYS * 24 * 60 * 60 * 1000) return 'Vence pronto'
  return null
}

export function ChannelAccountsPanel({ activeSociedadId }: Props) {
  const queryClient = useQueryClient()
  const [form, setForm] = useState(emptyForm)
  const [error, setError] = useState<string | null>(null)
  const [showManualForm, setShowManualForm] = useState(!EMBEDDED_SIGNUP_CONFIGURED)

  const query = useQuery({
    queryKey: ['channel-accounts', activeSociedadId],
    queryFn: () => fetchChannelAccounts(activeSociedadId),
  })

  const createMutation = useMutation({
    mutationFn: createChannelAccount,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['channel-accounts', activeSociedadId] })
      setForm(emptyForm)
    },
    onError: (err) => {
      if (axios.isAxiosError(err) && err.response?.status === 409) {
        setError('Ya existe un número registrado con ese phone_number_id de Meta.')
      } else {
        setError('No se pudo guardar el número. Revisa los datos e intenta de nuevo.')
      }
    },
  })

  const toggleMutation = useMutation({
    mutationFn: ({ id, is_active }: { id: string; is_active: boolean }) => updateChannelAccount(id, { is_active }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['channel-accounts', activeSociedadId] }),
  })

  const [tokenDialogAccount, setTokenDialogAccount] = useState<ChannelAccount | null>(null)
  const [tokenValue, setTokenValue] = useState('')

  const tokenMutation = useMutation({
    mutationFn: ({ id, access_token }: { id: string; access_token: string }) =>
      updateChannelAccount(id, { access_token }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['channel-accounts', activeSociedadId] })
      toast.success('Token actualizado. El bot ya puede volver a responder por este número.')
      setTokenDialogAccount(null)
      setTokenValue('')
    },
    onError: () => toast.error('No se pudo actualizar el token.'),
  })

  function handleSubmit(e: FormEvent): void {
    e.preventDefault()
    setError(null)
    createMutation.mutate({
      display_name: form.display_name || undefined,
      phone_number: form.phone_number,
      meta_phone_number_id: form.meta_phone_number_id,
      meta_waba_id: form.meta_waba_id,
      access_token: form.access_token,
    })
  }

  return (
    <div className="flex flex-col gap-6 lg:flex-row">
      <div className="flex w-full flex-col gap-3 rounded-xl border border-border bg-card p-5 lg:max-w-sm">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-foreground">
          <Phone className="h-4 w-4" />
          Nuevo número de WhatsApp
        </h2>

        <EmbeddedSignupButton sociedadId={activeSociedadId} />

        <button
          type="button"
          onClick={() => setShowManualForm((v) => !v)}
          className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground"
        >
          {showManualForm ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
          Configuración manual / avanzada
        </button>

        {showManualForm && (
          <form onSubmit={handleSubmit} className="flex flex-col gap-3 border-t border-border pt-3">
            <input
              type="text"
              placeholder="Nombre para identificarlo (opcional)"
              value={form.display_name}
              onChange={(e) => setForm({ ...form, display_name: e.target.value })}
              className={inputClass}
            />
            <input
              type="text"
              required
              placeholder="Número (+50600000000)"
              value={form.phone_number}
              onChange={(e) => setForm({ ...form, phone_number: e.target.value })}
              className={inputClass}
            />
            <input
              type="text"
              required
              placeholder="phone_number_id de Meta"
              value={form.meta_phone_number_id}
              onChange={(e) => setForm({ ...form, meta_phone_number_id: e.target.value })}
              className={inputClass}
            />
            <input
              type="text"
              required
              placeholder="WABA id de Meta"
              value={form.meta_waba_id}
              onChange={(e) => setForm({ ...form, meta_waba_id: e.target.value })}
              className={inputClass}
            />
            <input
              type="password"
              required
              placeholder="Access token de la app de Meta"
              value={form.access_token}
              onChange={(e) => setForm({ ...form, access_token: e.target.value })}
              className={inputClass}
            />

            {error && (
              <p className="rounded-lg bg-destructive/10 px-3 py-2 text-xs font-medium text-destructive">{error}</p>
            )}

            <Button type="submit" disabled={createMutation.isPending} className="mt-1">
              <Plus className="h-4 w-4" />
              Agregar número
            </Button>
          </form>
        )}
      </div>

      <div className="flex-1 overflow-x-auto rounded-xl border border-border bg-card">
        {query.isLoading && (
          <div className="flex flex-col gap-3 p-5">
            <Skeleton className="h-5 w-full" />
            <Skeleton className="h-5 w-5/6" />
          </div>
        )}
        {query.isError && <p className="p-5 text-sm text-destructive">No se pudieron cargar los números.</p>}
        {query.data && query.data.length === 0 && (
          <p className="p-5 text-sm text-muted-foreground">Todavía no hay ningún número de WhatsApp conectado.</p>
        )}
        {query.data && query.data.length > 0 && (
          <table className="w-full text-left text-sm">
            <thead className="border-b border-border text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-4 py-3 font-medium">Nombre</th>
                <th className="px-4 py-3 font-medium">Número</th>
                <th className="px-4 py-3 font-medium">phone_number_id</th>
                <th className="px-4 py-3 font-medium">Estado</th>
                <th className="px-4 py-3 font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {query.data.map((account: ChannelAccount) => (
                <tr key={account.id} className="border-b border-border/60 last:border-0">
                  <td className="px-4 py-3 text-foreground">{account.display_name || '—'}</td>
                  <td className="px-4 py-3 text-foreground">{account.phone_number}</td>
                  <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{account.meta_phone_number_id}</td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <Badge variant={account.is_active ? 'default' : 'secondary'}>
                        {account.is_active ? 'Activo' : 'Inactivo'}
                      </Badge>
                      {tokenExpirationWarning(account.token_expires_at) && (
                        <Badge variant="destructive">{tokenExpirationWarning(account.token_expires_at)}</Badge>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-1">
                      {account.token_expires_at && (
                        <EmbeddedSignupButton
                          sociedadId={activeSociedadId}
                          channelAccountId={account.id}
                          label="Reconectar"
                        />
                      )}
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setTokenValue('')
                          setTokenDialogAccount(account)
                        }}
                      >
                        <KeyRound className="h-3.5 w-3.5" />
                        Actualizar token
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => toggleMutation.mutate({ id: account.id, is_active: !account.is_active })}
                      >
                        {account.is_active ? <PowerOff className="h-3.5 w-3.5" /> : <PowerOn className="h-3.5 w-3.5" />}
                        {account.is_active ? 'Desactivar' : 'Activar'}
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Dialog open={!!tokenDialogAccount} onOpenChange={(next) => !next && setTokenDialogAccount(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Actualizar token — {tokenDialogAccount?.display_name || tokenDialogAccount?.phone_number}</DialogTitle>
            <DialogDescription>
              Los access tokens de prueba de Meta vencen. Pega el nuevo token para este número sin tener que darlo de
              alta de nuevo (el phone_number_id de Meta es único y ya está registrado).
            </DialogDescription>
          </DialogHeader>

          <input
            type="password"
            autoFocus
            placeholder="Nuevo access token de la app de Meta"
            value={tokenValue}
            onChange={(e) => setTokenValue(e.target.value)}
            className={inputClass}
          />

          <DialogFooter>
            <Button variant="outline" onClick={() => setTokenDialogAccount(null)}>
              Cancelar
            </Button>
            <Button
              disabled={!tokenValue || tokenMutation.isPending}
              onClick={() => tokenMutation.mutate({ id: tokenDialogAccount!.id, access_token: tokenValue })}
            >
              Guardar token
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
